/**
 * Web Storage that cannot break the app.
 *
 * Nothing here throws. Storage is absent in a node test, refused outright in a
 * private window, and full once a quota is reached; a value inside it may have
 * been written by an older build, hand-edited, or truncated. None of that may
 * reach a render. A throw inside a `useState` initializer white-pages the
 * whole tree, and because the bad value is never rewritten it throws again on
 * every reload — the app stays bricked for that browser profile until storage
 * is cleared by hand.
 *
 * A read answers `null` when it cannot produce a value; a write and a remove
 * are no-ops when storage refuses them. Callers that must survive a malformed
 * value still have to validate what they get back: `readJson` returns
 * `unknown` precisely so they cannot forget, and `isPlainObject` is the guard
 * that usually starts it.
 *
 * The underlying store is resolved per call rather than captured at import,
 * so a test may install one after this module is loaded, and so a browser
 * that refuses storage refuses it at the call and not at boot.
 */

type StorageKind = 'local' | 'session';

/** Every way to touch one of the two Web Storage areas, all of them safe. */
export interface WebStorage {
    /** The stored string, or null when absent or unreadable. */
    readText(key: string): string | null;
    writeText(key: string, value: string): void;
    /** The parsed value, or null when absent, unreadable, or not valid JSON. */
    readJson(key: string): unknown;
    writeJson(key: string, value: unknown): void;
    remove(key: string): void;
    /** Every key currently held, or none when storage is unreadable. */
    listKeys(): string[];
    removeByPrefix(prefix: string): void;
}

function getStorageArea(kind: StorageKind): Storage | null {
    try {
        const store = kind === 'local' ? globalThis.localStorage : globalThis.sessionStorage;
        return store ?? null;
    } catch {
        return null; //< a private window can refuse the property itself
    }
}

function makeWebStorage(kind: StorageKind): WebStorage {
    return {
        readText(key: string): string | null {
            try {
                return getStorageArea(kind)?.getItem(key) ?? null;
            } catch {
                return null;
            }
        },

        writeText(key: string, value: string): void {
            try {
                getStorageArea(kind)?.setItem(key, value);
            } catch {
                /* quota, or storage refused */
            }
        },

        readJson(key: string): unknown {
            const rawText = this.readText(key);
            if (rawText === null) {
                return null;
            }
            try {
                return JSON.parse(rawText) as unknown;
            } catch {
                return null;
            }
        },

        writeJson(key: string, value: unknown): void {
            let encodedText: string;
            try {
                encodedText = JSON.stringify(value);
            } catch {
                return; //< a cycle or a BigInt; there is nothing to store
            }
            this.writeText(key, encodedText);
        },

        remove(key: string): void {
            try {
                getStorageArea(kind)?.removeItem(key);
            } catch {
                /* storage refused */
            }
        },

        listKeys(): string[] {
            try {
                const store = getStorageArea(kind);
                return store ? Object.keys(store) : [];
            } catch {
                return [];
            }
        },

        removeByPrefix(prefix: string): void {
            for (const key of this.listKeys()) {
                if (key.startsWith(prefix)) {
                    this.remove(key);
                }
            }
        },
    };
}

export const localStore: WebStorage = makeWebStorage('local');
export const sessionStore: WebStorage = makeWebStorage('session');

/**
 * Whether a parsed value is an object worth reading fields off — the guard a
 * `readJson` result usually passes through first. Arrays are excluded: every
 * caller here stores a record, and an array would satisfy `typeof` while
 * answering `undefined` to every field.
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
